import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Coins, Users, ShieldCheck, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/constants";
import { ChessLogo } from "@/components/ChessLogo";
import { AuthModal } from "@/components/AuthModal";
import { InstagramSection } from "@/components/InstagramSection";
import { Button } from "@/components/ui/button";

function StatTicker({ stats }) {
  const items = [
    { label: "Issues raised", value: stats?.total_issues ?? 0 },
    { label: "Citizens joined", value: stats?.total_members ?? 0 },
    { label: "Dimagi coins backed", value: stats?.coins_backed ?? 0 },
    { label: "Issues solved", value: stats?.solved ?? 0 },
  ];
  const row = [...items, ...items];
  return (
    <div data-testid="hero-stats-banner" className="overflow-hidden border-y border-border bg-card/60">
      <div className="flex gap-10 py-4 animate-marquee whitespace-nowrap w-max">
        {row.map((it, i) => (
          <div key={i} className="flex items-baseline gap-2 px-2">
            <span className="font-mono font-bold text-xl text-saffron">{it.value.toLocaleString("en-IN")}</span>
            <span className="text-sm text-muted-foreground uppercase tracking-wider">{it.label}</span>
            <span className="text-border">◆</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const join = () => (user ? navigate("/feed") : setAuthOpen(true));

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 chess-grid opacity-60" />
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-saffron/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald_dnjd/20 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 pt-16 pb-14 sm:pt-24 sm:pb-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-6">
              <ChessLogo size={52} />
              <div>
                <div className="font-display font-extrabold text-lg leading-none">Dimagi National Zanata Dal</div>
                <div className="text-xs text-muted-foreground font-mono tracking-widest">SYMBOL: THE CHESS BOARD ♟</div>
              </div>
            </div>

            <h1 data-testid="hero-headline" className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">
              Vote for <span className="text-saffron">issues</span>,<br className="hidden sm:block" /> not <span className="line-through decoration-destructive/70 decoration-4">ideology</span>.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              A movement where every citizen gets a private pseudo-identity and a Dimagi coin — and
              a real voice to raise local problems: roads, schools, stray animals, health, electricity and garbage.
              No slogans. Just strategy, like a game of chess.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button data-testid="hero-cta-join" size="lg" onClick={join}
                className="bg-saffron text-primary-foreground hover:bg-saffron/90 font-semibold gap-2 text-base h-12 px-7">
                {user ? "Go to feed" : "Join free — get 1 Dimagi coin"} <ArrowRight size={18} />
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate("/feed")} className="h-12 px-7 text-base">
                Explore issues
              </Button>
            </div>
          </motion.div>
        </div>

        <StatTicker stats={stats} />
      </section>

      {/* Mission */}
      <section id="mission" className="max-w-6xl mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-saffron">The DNZD idea</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              Issue-based politics, played like chess
            </h2>
            <p className="mt-4 text-muted-foreground">
              Ideology divides. Issues unite. DNZD is a citizens' platform where your identity stays
              protected, your voice stays powerful, and every move is about solving a real problem in your locality.
            </p>
            <div className="mt-6 space-y-4">
              {[
                { icon: ShieldCheck, t: "Pseudo identity, real trust", d: "Sign up privately. Your public alias (auto-generated or your own) keeps your real name safe." },
                { icon: Coins, t: "The Dimagi coin economy", d: "Every member gets 1 Dimagi coin. Raise an issue to earn more. Spend coins to back the issues that matter." },
                { icon: Users, t: "Power to the local voice", d: "Roads, schools, strays, health, electricity, garbage — the issues that actually touch daily life." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <div className="shrink-0 grid place-items-center h-11 w-11 rounded-xl bg-saffron/15 text-saffron">
                    <f.icon size={20} />
                  </div>
                  <div>
                    <div className="font-semibold">{f.t}</div>
                    <div className="text-sm text-muted-foreground">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CATEGORIES.map((c, i) => (
              <motion.button
                key={c.key}
                onClick={() => navigate("/feed")}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group rounded-2xl border border-border bg-card p-5 text-left hover:border-saffron/50 transition-colors"
              >
                <div className="grid place-items-center h-12 w-12 rounded-xl mb-3 text-white" style={{ background: c.color }}>
                  <c.icon size={22} />
                </div>
                <div className="font-semibold">{c.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                  Raise an issue <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* How coins work */}
      <section className="max-w-6xl mx-auto px-4 pb-4">
        <div className="rounded-3xl border border-coin/30 bg-coin/5 p-8 sm:p-10">
          <div className="flex items-center gap-2 text-coin font-mono text-xs uppercase tracking-widest">
            <Sparkles size={14} /> How the Dimagi coin works
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-6">
            {[
              { n: "1", t: "Join → get 1 coin", d: "Your founding Dimagi coin lands in your wallet the moment you sign up." },
              { n: "+3", t: "Raise an issue → earn", d: "Every genuine local issue you post rewards you with 3 more Dimagi coins." },
              { n: "−1", t: "Back an issue → spend", d: "Spend a coin to back another citizen's issue. They earn it, and the issue rises." },
            ].map((s) => (
              <div key={s.t}>
                <div className="font-mono font-extrabold text-3xl text-coin">{s.n}</div>
                <div className="mt-1 font-semibold">{s.t}</div>
                <div className="text-sm text-muted-foreground">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <InstagramSection />

      <footer className="border-t border-border">
        <div className="tricolor-bar h-1 w-full" />
        <div className="max-w-6xl mx-auto px-4 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ChessLogo size={34} />
            <div className="text-sm">
              <div className="font-display font-bold">Dimagi National Zanata Dal</div>
              <div className="text-xs text-muted-foreground">Issue-based politics for a smarter India.</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground font-mono">© {new Date().getFullYear()} DNZD · A citizens' movement</div>
        </div>
      </footer>

      <AuthModal open={authOpen} tab="register" onOpenChange={setAuthOpen} />
    </div>
  );
}
